import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canAdministerFleet, canManageFleet } from "../../auth/roleView";
import { useHosts, useProviderCredentials } from "../../api/queries";
import type { Environment, Host, Instance } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { HostStatePill, NodeTypeBadge } from "../../components/StatePill";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useEnvironmentsById } from "../environments/useEnvironmentsById";
import { FleetSubNav } from "../fleet/FleetSubNav";
import { AddHostModal } from "../nodes/AddHostModal";
import { ProvisionInstanceModal } from "../instances/ProvisionInstanceModal";
import { formatHostWhere } from "./hostFormatting";
import { ProvisioningProgress } from "./ProvisioningProgress";
import "../../components/Form.css";
import "./hosts-list.css";

/**
 * The unified fleet list (host/deploy UX review, wireframe B): ONE table, ONE
 * "Host" object per row — replacing the separate Instances (provisioning) and
 * Nodes (live agent) tables this app used to show side by side. Every row
 * carries both facets when it has them; a still-provisioning host or a BYO
 * host simply shows the facet it has (see `formatHostWhere` and the
 * AGENT/CONT. columns below) rather than an empty row in a second table.
 */
function buildColumns(environmentsById: Map<string, Environment>): DataTableColumn<Host>[] {
  return [
    {
      key: "status",
      header: "Status",
      // A provisioning/enrolling/failed row carries live progress framing
      // BELOW the pill (Host/Deploy UX Phase 3 — the design review's "a red
      // pill and nothing else" / "shown as a spreadsheet cell" complaint) —
      // `ProvisioningProgress` renders nothing for every other state, so an
      // online/offline/BYO row looks exactly as it did before.
      cell: (h) => (
        <div className="host-status-cell">
          <HostStatePill state={h.state} />
          <ProvisioningProgress host={h} variant="list" />
        </div>
      ),
    },
    {
      key: "name",
      header: "Name",
      cell: (h) => (
        <span className="data-table__name">
          {/* stopPropagation: the row itself is also a click target (DataTable's
              onRowClick); without this, activating the link would navigate twice. */}
          <Link className="data-table__primary-link" href={`/hosts/${h.id}`} onClick={(e) => e.stopPropagation()}>
            {h.name}
          </Link>
          <NodeTypeBadge nodeType={h.nodeType} />
        </span>
      ),
      className: "data-table__primary",
    },
    {
      key: "env",
      header: "Env",
      cell: (h) =>
        h.environmentName ? (
          <EnvironmentBadge name={h.environmentName} envClass={environmentsById.get(h.environmentId)?.class} />
        ) : (
          "—"
        ),
      className: (h) => (h.environmentName ? undefined : "data-table__empty-cell"),
    },
    { key: "where", header: "Where", cell: (h) => formatHostWhere(h) },
    {
      key: "agent",
      header: "Agent",
      // A connected agent whose self-reported metadata hasn't arrived yet (the CP's
      // heartbeat store is in-memory, so it is empty right after a CP restart) must
      // read as AWAITING, never as "not enrolled" — the row's very presence with an
      // Online pill proves enrollment, so that label was a lying surface (A-10). A
      // real heartbeat always carries a version, so a missing version IS the
      // no-heartbeat-yet signal.
      cell: (h) => (h.liveAgent ? (h.liveAgent.agentVersion ?? "awaiting heartbeat") : "—"),
      className: (h) => (h.liveAgent?.agentVersion ? "data-table__mono" : "data-table__empty-cell"),
    },
    {
      key: "containers",
      header: "Cont.",
      // No heartbeat yet ⇒ the count is UNKNOWN, not zero — a hard "0" before the
      // first heartbeat lands is a lying number (A-10).
      cell: (h) => (h.liveAgent ? (h.liveAgent.agentVersion ? h.liveAgent.containerCount : "—") : "—"),
      className: (h) => (h.liveAgent ? undefined : "data-table__empty-cell"),
    },
    { key: "runs", header: "Runs", cell: (h) => h.servicesPlacedCount, className: "data-table__secondary" },
  ];
}

export function HostsListPage() {
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch, isFetching } = useHosts({ cursor: paginator.cursor });
  const { data: credentialsPage } = useProviderCredentials();
  // Resolves a host's environment to its policy CLASS for the Env column's
  // badge (Host/Deploy UX Phase 4, #526) — `Host` already carries
  // `environmentId`/`environmentName` (P1), but only `Environment` itself
  // carries `class`.
  const environmentsById = useEnvironmentsById();

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const columns = buildColumns(environmentsById);

  // instance:create (provisioning) is granted to operator AND admin; minting
  // a join token (connecting an existing host) is node:create, admin-only —
  // mirrors InstancesListPage's gate exactly, unchanged by unifying the view
  // (S-8: this is UX only, the server enforces the real decision on every
  // request).
  const canProvision = canManageFleet(session?.role);
  const canConnect = canAdministerFleet(session?.role);
  const hasCredentials = (credentialsPage?.items.length ?? 0) > 0;

  // Provision flow continuity (Host/Deploy UX Phase 3): rather than leaving
  // the operator on this static list after submitting, jump straight into
  // the new host's live progress view. `agentId` is the host's reserved
  // future identity — set BEFORE the provider call (see `Instance.agentId`'s
  // doc comment, types.ts) — so it's already the right id to route to even
  // though no node has enrolled yet; falling back to `instance.id` covers a
  // (real-world only) row written before that linkage existed.
  function handleProvisioned(instance: Instance) {
    navigate(`/hosts/${instance.agentId ?? instance.id}`);
  }

  const provisionButton = (
    <Button variant="primary" onClick={() => setProvisionOpen(true)} disabled={!hasCredentials}>
      Provision new
    </Button>
  );
  const connectButton = (
    <Button variant="secondary" onClick={() => setConnectOpen(true)}>
      Connect existing
    </Button>
  );

  return (
    <>
      <FleetSubNav activeTab="hosts" />
      <PageHeader
        title="Machines"
        description="Every machine running your workloads — whether Comebye provisioned it or you connected one you already had. Kelpie only ever connects outbound; there's nothing to open on the machine itself."
        actions={
          canProvision || canConnect ? (
            <>
              {canConnect && connectButton}
              {canProvision && provisionButton}
            </>
          ) : (
            <span className="page-header__hint">Ask an operator or admin to add machines.</span>
          )
        }
      />

      {canProvision && !hasCredentials && (
        <p className="form-note">
          Add a <Link href="/govern/credentials/provider">provider credential</Link> to provision new machines — or connect
          one you already have with "Connect existing".
        </p>
      )}

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading machines…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={error instanceof Error ? error.message : "Couldn't load machines. Check your connection and try again."}
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No machines yet"
            description="Provision a new server or connect one you already run to give your services somewhere to go."
            action={
              canProvision ? provisionButton : canConnect ? connectButton : undefined
            }
          />
        }
      >
        {data && (
          <>
            <DataTable
              columns={columns}
              rows={data.items}
              rowKey={(h) => h.id}
              onRowClick={(h) => navigate(`/hosts/${h.id}`)}
              ariaBusy={isFetching}
            />
            <PaginatorControls itemCount={data.items.length} nextCursor={data.nextCursor} paginator={paginator} noun="machines" />
          </>
        )}
      </QueryStates>

      {canProvision && (
        <ProvisionInstanceModal
          open={provisionOpen}
          onClose={() => setProvisionOpen(false)}
          onProvisioned={handleProvisioned}
        />
      )}
      {canConnect && <AddHostModal open={connectOpen} onClose={() => setConnectOpen(false)} />}
    </>
  );
}
